/**
 * API client — central place dla wszystkich requestów do Capibara backendu.
 *
 * Sesja 33 (4.C frontend): pierwszy klient API. Endpointy `/auth/*` z backend
 * sesji 32. Endpointy ekonomiczne (egg/hatch/merge/equip) dorzucone w 4.D.
 *
 * KLUCZOWE:
 *  - `credentials: 'include'` — bez tego cookie z `/auth/login` nie zostanie
 *    wysłane w kolejnych requestach (cross-origin Vite 5173 → API 3030).
 *  - `VITE_API_BASE_URL` z env. Dev: `http://localhost:3030`. Prod: relative ''.
 *    (Po fix HIGH 1 sesji 33 — `.env.development.local`, nie `.env.local`.)
 *  - `ApiError` — strukturalny błąd z server response (status + code + field + issues).
 *  - **Global 401 handler** (Codex MED 2 sesji 33): kazdy 401 z protected endpointu
 *    invaliduje store auth state + robi `location.reload()` -> bootstrap zrobi
 *    auto-guest (sesja guest core), wiec gracz nie wisi z brakiem sesji.
 *    `me()` ma opt-out flag bo 401 to dla niej "nie zalogowany", nie blad.
 *  - **`Retry-After` header parsing** (Codex MED 3 sesji 33): per-route fastify
 *    rate-limit zwraca header, nie body field. Sklejamy oba w `retryAfterMs`.
 */

import { useGameStore } from '@/ui/store/gameStore';
import type { Variant } from '@/domain/types/common';
import type { StallSale } from '@/ui/stallSales';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

/** Strukturalny błąd z API. Klient mapuje na user-friendly message. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly field?: string;
  public readonly issues?: unknown[];
  public readonly retryAfterMs?: number;

  constructor(opts: {
    status: number;
    code: string;
    message: string;
    field?: string;
    issues?: unknown[];
    retryAfterMs?: number;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.code = opts.code;
    if (opts.field !== undefined) this.field = opts.field;
    if (opts.issues !== undefined) this.issues = opts.issues;
    if (opts.retryAfterMs !== undefined) this.retryAfterMs = opts.retryAfterMs;
  }
}

interface ApiErrorResponse {
  error?: string;
  field?: string;
  issues?: unknown[];
  retryAfterMs?: number;
  scope?: string;
  // @fastify/rate-limit per-route default body:
  statusCode?: number;
  message?: string;
}

interface RequestInitExtra extends RequestInit {
  json?: unknown;
  /**
   * Opt-out z globalnego 401 handler'a. Domyślnie każdy 401 → setAuth(false,null).
   * `authApi.me()` ustawia true bo 401 to dla niej semantycznie "nie zalogowany",
   * nie błąd auth invalidacji (user moze byc świeży po cold start).
   */
  skipAuthInvalidation?: boolean;
}

/**
 * Parsuje `Retry-After` header HTTP. Zwraca ms lub undefined.
 * RFC 7231: format to liczba sekund LUB date string. Obsługujemy oba.
 */
function parseRetryAfterHeader(header: string | null): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.floor(seconds * 1000);
  }
  // Date format fallback
  const dateMs = Date.parse(header);
  if (Number.isFinite(dateMs)) {
    const delta = dateMs - Date.now();
    return delta > 0 ? delta : 0;
  }
  return undefined;
}

/**
 * Niskopoziomowy fetch wrapper. Wszystkie request idą przez to:
 *  - automatic JSON body serialization
 *  - credentials:include dla httpOnly cookie
 *  - error mapping na ApiError z server response shape
 *  - network failures → ApiError(status=0, code='network_error')
 *  - global 401 → setAuth(false, null) (chyba ze skipAuthInvalidation=true)
 *  - Retry-After header parsing dla 429 (fallback gdy body nie ma retryAfterMs)
 */
async function request<T>(
  path: string,
  init: RequestInitExtra = {},
): Promise<T> {
  const { json, headers, skipAuthInvalidation, ...rest } = init;
  const body = json !== undefined ? JSON.stringify(json) : init.body;

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      body,
      credentials: 'include',
      headers: {
        ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
    });
  } catch (err) {
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: (err as Error)?.message ?? 'Network request failed',
    });
  }

  // 204 No Content lub puste body → zwracamy {} jako T.
  if (res.status === 204) {
    return {} as T;
  }

  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch {
    parsed = {};
  }

  if (!res.ok) {
    const errBody = (parsed ?? {}) as ApiErrorResponse;

    // Codex MED 3 sesji 33: per-route @fastify/rate-limit zwraca `retry-after`
    // header zamiast body fieldu. Sklejamy body field jesli jest, fallback do
    // header.
    const retryAfterMs =
      errBody.retryAfterMs ?? parseRetryAfterHeader(res.headers.get('retry-after'));

    // code z body.error (nasz wewnetrzny shape) lub body.error string (fastify
    // rate-limit default "Too Many Requests"). Normalizujemy do snake_case
    // żeby klient nie musial znac obu konwencji.
    const rawCode = errBody.error ?? `http_${res.status}`;
    const normalizedCode =
      typeof rawCode === 'string' && rawCode.includes(' ')
        ? rawCode.toLowerCase().replace(/\s+/g, '_')
        : rawCode;

    const apiErr = new ApiError({
      status: res.status,
      code: normalizedCode,
      message: errBody.message ?? errBody.error ?? `HTTP ${res.status}`,
      ...(errBody.field !== undefined && { field: errBody.field }),
      ...(errBody.issues !== undefined && { issues: errBody.issues }),
      ...(retryAfterMs !== undefined && { retryAfterMs }),
    });

    // Codex MED 2 sesji 33: global 401 handler invaliduje auth state.
    // authApi.me()/login() opt-outuja (401 = "anon"/"zle haslo", nie "expired").
    //
    // Sesja 34B HIGH 2 fix: po auth-loss robimy TWARDY RELOAD strony. Bez tego
    // `saveLoaded` zostaje true a `saveManager.current` trzyma stan poprzedniego
    // konta - re-login w tej samej karcie ominalby LoadingScreen i nowy user
    // dostalby (i nadpisal PUT-em) cudzy save. Reload = czysty bootstrap:
    // resolveSession (auto-guest) -> swiezy saveManager.load() dla nowego usera.
    if (res.status === 401 && !skipAuthInvalidation) {
      try {
        useGameStore.getState().setAuth(false, null);
      } catch (storeErr) {
        if (import.meta.env.DEV) {
          console.warn('[api] setAuth(false) w 401 handler rzucil — nieoczekiwane:', storeErr);
        }
      }
      // Reload po wyczyszczeniu auth state. Browser przerwie bieżący JS -
      // `throw apiErr` ponizej moze nie zdazyc, to OK (reload wygrywa).
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    }

    throw apiErr;
  }

  return parsed as T;
}

// =============================================================================
// /auth/* endpointy
// =============================================================================

export interface AuthUser {
  id: string;
  username: string;
  created_at?: string;
  /** Konto-gość (utworzone przez `/auth/guest`) vs pełne konto. */
  is_guest: boolean;
  /** Rozwiązany display name (5.0.F): manualny / ładny nick gościa / username. */
  display_name?: string;
}

export interface RegisterInput {
  username: string;
  password: string;
  email?: string;
}

export interface LoginInput {
  username: string;
  password: string;
}

export interface MeOptions {
  /** AbortSignal dla timeoutu (sesja 33 FIXES MED 1 - bootstrap deadline). */
  signal?: AbortSignal;
}

export const authApi = {
  /**
   * POST /auth/register
   * Sukces: 201 + cookie set. Server zwraca `{user: {id, username}}`.
   * Błędy:
   *   - 400 validation_failed (issues array z zod)
   *   - 409 conflict (field: 'username' | 'email')
   *   - 429 rate_limited (retryAfterMs lub Retry-After header)
   */
  async register(input: RegisterInput): Promise<AuthUser> {
    const res = await request<{ user: AuthUser }>('/auth/register', {
      method: 'POST',
      json: input,
    });
    return res.user;
  },

  /**
   * POST /auth/guest — tworzy automatyczne konto-gość (tryb gościa).
   * Klient przekazuje losowe hasło (zapisywane w localStorage do re-loginu).
   * Sukces: 201 + cookie. Server zwraca `{user:{id,username}}` — username to
   * wygenerowane `guest_<hex>`.
   */
  async createGuest(password: string): Promise<AuthUser> {
    const res = await request<{ user: AuthUser }>('/auth/guest', {
      method: 'POST',
      json: { password },
      skipAuthInvalidation: true,
    });
    return res.user;
  },

  /**
   * POST /auth/claim — przekształca konto-gościa w pełne konto.
   * Identyczna walidacja jak register (username + password + opcjonalny email).
   * Sesja (cookie) zostaje aktywna — `is_guest` w zwróconym user przechodzi na
   * `false`, username/hasło zmienione. Save/pety/monety bez zmian (ten sam id).
   * Błędy: 409 conflict (username/email), 409 not_a_guest (pełne konto),
   * 400 validation_failed.
   */
  async claim(input: RegisterInput): Promise<AuthUser> {
    const res = await request<{ user: AuthUser }>('/auth/claim', {
      method: 'POST',
      json: input,
    });
    return res.user;
  },

  /**
   * POST /auth/login
   * Sukces: 200 + cookie set.
   * Błędy:
   *   - 400 validation_failed
   *   - 401 invalid_credentials (timing-safe: brak rozróżnienia "nie ma usera" vs "złe hasło")
   *   - 429 rate_limited (per-IP+username albo global per-IP)
   */
  async login(input: LoginInput): Promise<AuthUser> {
    // skipAuthInvalidation=true: 401 podczas login to "zle haslo", nie
    // "sesja wygasla". Nie chcemy invalidatowac (nieistniejacego) state.
    const res = await request<{ user: AuthUser }>('/auth/login', {
      method: 'POST',
      json: input,
      skipAuthInvalidation: true,
    });
    return res.user;
  },

  /**
   * POST /auth/logout
   * Sukces: 200 cookie cleared. Idempotent (logout bez cookie też OK).
   */
  async logout(): Promise<void> {
    await request<{ ok: true }>('/auth/logout', { method: 'POST' });
  },

  /**
   * GET /auth/me
   * Sukces: 200 `{user: {id, username, created_at}}`.
   * Błąd 401: zwracamy `null` (klient interpretuje jako "nie zalogowany").
   * Inne błędy (5xx, network): rzucamy ApiError.
   *
   * Opcja `signal` (sesja 33 FIXES MED 1): bootstrap probe ma AbortController
   * z 5s deadline zeby resolveSession nie wisial za dlugo przy slow network.
   */
  async me(opts: MeOptions = {}): Promise<AuthUser | null> {
    try {
      const init: RequestInitExtra = {
        method: 'GET',
        skipAuthInvalidation: true, // 401 z /me to "anon", nie "expired session"
      };
      if (opts.signal) init.signal = opts.signal;
      const res = await request<{ user: AuthUser }>('/auth/me', init);
      return res.user;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        return null;
      }
      throw err;
    }
  },

  /**
   * PUT /auth/me/display-name — ustaw manualny display name (5.0.F).
   * Tylko pełne konto. Błędy: 400 invalid_display_name, 403
   * guest_cannot_change_name, 429 display_name_change_limit.
   */
  async setDisplayName(displayName: string): Promise<{ display_name: string }> {
    return request<{ display_name: string }>('/auth/me/display-name', {
      method: 'PUT',
      json: { display_name: displayName },
    });
  },
};

// =============================================================================
// /saves/* endpointy (sesja 34B)
// =============================================================================

import type { PlayerSave } from '@/save/PlayerSave.schema';

/**
 * Odpowiedź `GET /saves/me`:
 *  - `{ initialized: false }` — świeże konto, save to placeholder (po register).
 *    Klient musi zaimportować lokalny IDB save albo utworzyć fresh.
 *  - `{ initialized: true, save }` — konto ma zainicjalizowany PlayerSave.
 */
export type GetSaveResult =
  | { initialized: false }
  | { initialized: true; save: PlayerSave; save_rev: number };

export const saveApi = {
  /**
   * GET /saves/me — pełny stan gracza albo `{initialized:false}`.
   * Wymaga ważnej sesji (cookie). 401 → ApiError (global handler invaliduje auth).
   */
  async me(): Promise<GetSaveResult> {
    return request<GetSaveResult>('/saves/me', { method: 'GET' });
  },

  /**
   * PUT /saves/me — zapis pełnego stanu (blob PUT przejściowy, sesja 34A).
   * Server liczy power petów sam, waliduje typy + sanity bounds.
   */
  async put(save: PlayerSave): Promise<void> {
    await request<{ ok: true; savedAt: number }>('/saves/me', {
      method: 'PUT',
      json: save,
    });
  },

  /**
   * POST /saves/import — jednorazowy import lokalnego IDB save.
   * Idempotent: konto już zainicjalizowane → `{imported:false, reason}`.
   */
  async import(save: PlayerSave): Promise<{ imported: boolean; reason?: string }> {
    return request<{ imported: boolean; reason?: string }>('/saves/import', {
      method: 'POST',
      json: save,
    });
  },

  /**
   * DELETE /saves/me — reset progresu (czyści pety/jajka, Save → placeholder).
   * Konto + auth zostają. Po tym `me()` zwróci `{initialized:false}`.
   */
  async remove(): Promise<void> {
    await request<{ ok: true }>('/saves/me', { method: 'DELETE' });
  },
};

// =============================================================================
// Endpointy ekonomii (sesja 35B, Step 4.D.1) — granularne, server-authoritative
// =============================================================================
// Każdy endpoint waliduje + mutuje stan w transakcji pod row-lockiem i zwraca
// pełny PlayerSave po mutacji (jak `GET /saves/me`). Klient adoptuje go jako
// źródło prawdy (`saveManager.adoptServerState`) — patrz `economyActions.ts`.
//
// Backend liczy RNG hatcha, koszty, capy — klient NIE jest zaufany. Modale
// robiły wcześniej `saveManager.update(recipe)` (blob PUT), ale po sesji 35A
// PUT `skipEntities` celowo NIE zapisuje pets/eggs/upgrades/biomów — granularne
// endpointy to jedyna droga mutacji ekonomii.

/** Wynik wyklucia jajka z `POST /eggs/:uid/hatch`. */
export interface HatchResultDTO {
  newPetUid: string;
  petId: string;
  variant: string;
  isHuge: boolean;
  eggDefId: string;
}

/**
 * Pojedyncza pozycja w response `POST /eggs/hatch-bulk` (5.D.3b).
 * `forcedByGuarantee=true` gdy bulk minRarityOnBulk override'ował ten roll.
 */
export interface HatchBulkEntryDTO {
  newPetUid: string;
  sourceEggUid: string;
  petId: string;
  variant: string;
  isHuge: boolean;
  autoEquipped: boolean;
  forcedByGuarantee: boolean;
}

/** Wynik łączenia petów z `POST /pets/merge`. */
export interface MergeResultDTO {
  newPetUid: string;
  defId: string;
  variant: string;
}

export interface BulkMergeRequestGroup {
  defId: string;
  variant: 'normal' | 'gold';
  batches: number;
}

export interface BulkMergeResultDTO {
  defId: string;
  sourceVariant: 'normal' | 'gold';
  resultVariant: 'gold' | 'rainbow';
  batches: number;
  consumedCount: number;
  newPetUids: string[];
}

export interface BulkMergeResponse {
  changes: EconomyDelta;
  results: BulkMergeResultDTO[];
  totalBatches: number;
  gemsAwarded: number;
  save_rev: number;
}

/**
 * Odpowiedź `POST /world/tick` (Faza 1a — dieta payloadu, DESIGN_PERFORMANCE_SCALING.md §A1).
 * NIE zawiera już ekwipunku — tylko waluty + staty (world tick nie zmienia
 * petów/jajek). Klient patchuje te pola lokalnie przez `saveManager.patchServerScalars`.
 */
export interface WorldTickResult {
  currencies: PlayerSave['currencies'];
  /** EV-1b-2: portfel walut eventowych (server-authoritative) — np. { event_shells: 120 }. */
  eventCurrencies: PlayerSave['eventCurrencies'];
  stats: PlayerSave['stats'];
  gemsRolled: number;
  /** EV-1b-2: muszelki wyrolowane w TYM ticku (duplikat = 0) — pod ewentualny FX 🐚. */
  shellsRolled: number;
  /** Faza 1b-1 drift-guard — monotoniczny rev (klient ignoruje <= appliedRev). */
  save_rev: number;
}

/** Pojedyncze pety/jajka w delcie — DOKŁADNIE kształt pozycji `PlayerSave`,
 * żeby reduktor `applyEconomyDelta` wstawiał je do store bez konwersji. */
export type PetDelta = PlayerSave['inventory'][number];
export type EggDelta = PlayerSave['eggs'][number];

/**
 * Delta akcji ekonomii (Faza 1b-2, DESIGN_PERFORMANCE_SCALING.md §A2). Opisuje
 * „co się zmieniło" zamiast pełnego `PlayerSave` (~1500 petów). Reduktor
 * `applyEconomyDelta` aplikuje obecne pola chirurgicznie (immutable add/remove).
 * Pola opcjonalne — każdy endpoint wypełnia tylko swoje.
 */
export interface EconomyDelta {
  /** Absolutne waluty z serwera (nie delta) — reduktor ustawia HUD/coins/gems. */
  currencies?: PlayerSave['currencies'];
  /**
   * Portfel walut eventowych (id → ilość, absolutne z serwera). Wypełniany przez
   * endpointy eventu (EV-1b: drop muszelek). Reduktor merge'uje go do save'a,
   * `syncStoresFromSave` aktualizuje plakietkę HUD bez pełnego resyncu.
   */
  eventCurrencies?: PlayerSave['eventCurrencies'];
  stats?: PlayerSave['stats'];
  /** Pełne `eggStats` (pity/liczniki jajek) — hatch/hatch-bulk mutują `egg_stats`. */
  eggStats?: PlayerSave['eggStats'];
  /** Pełna lista equipped uidów (≤6) — wykrycie auto-equip po hatchu. */
  equipped?: string[];
  newEgg?: EggDelta;
  /** Wiele nowych jajek naraz (np. nagroda World Bossa = 4× royal egg). */
  newEggs?: EggDelta[];
  removedEggUid?: string;
  removedEggUids?: string[];
  newPet?: PetDelta;
  newPets?: PetDelta[];
  /** Usunięte pety (1b-3): merge konsumuje N, sell-to-npc usuwa 1. */
  removedPetUid?: string;
  removedPetUids?: string[];
  /** Zaktualizowany pet — replace po uid (1b-3 flags lock/favorite; przyszłe in_stall_id). */
  updatedPet?: PetDelta;
  updatedPets?: PetDelta[];
  /** Pełna mapa upgrades (replace) — upgrades/:id/purchase (1b-4). */
  upgrades?: PlayerSave['upgrades'];
  /** Pełna lista odblokowanych biomów (replace) — biomes/:id/unlock (1b-4). */
  biomesUnlocked?: PlayerSave['biomesUnlocked'];
  /** Aktualny biom — mała, autorytatywna delta z `biomes/:id/enter`. */
  currentBiomeId?: PlayerSave['currentBiomeId'];
  /** Patch flag (MERGE — nie clobber client-only flag) — np. starterPetsChosen (1b-4). */
  flags?: PlayerSave['flags'];
  /** Pełny plecak mikstur (replace) — /potions/buy i /potions/use. */
  potions?: PlayerSave['potions'];
  /** Pełna mapa aktywnych buffów (replace) — /potions/use. */
  activeEffects?: PlayerSave['activeEffects'];
  /**
   * Pełny stan eventów (replace) — hatch jajka eventu (odkrycie albumu) i claim
   * albumu mutują `events`. EV-3.
   */
  events?: PlayerSave['events'];
  /** Profil gracza (replace) — claim albumu odblokowuje awatary (unlockedSkins). EV-3. */
  profile?: PlayerSave['profile'];
}

export interface EventDailyTraderStatusResponse {
  event_id: string;
  event_active: boolean;
  can_claim: boolean;
  claimed_today: boolean;
  last_claim_day: string | null;
  last_claim_at: number | null;
  next_claim_at: string | null;
  streak_days: number;
  projected_streak_days: number;
  total_claimed: number;
  reward_currency_id: string;
  shells_available: number;
}

export interface EventDailyTraderClaimResponse {
  shells_received: number;
  shells_total: number;
  streak_days: number;
  next_claim_at: string;
  total_claimed: number;
  changes: EconomyDelta;
  save_rev: number;
}

export interface EventPierFishingClaimResponse {
  shells_received: number;
  shells_total: number;
  next_claim_at: string;
  total_claimed: number;
  changes: EconomyDelta;
  save_rev: number;
}

export const economyApi = {
  /**
   * POST /shop/buy-egg — kup jajko. `currency` (opcjonalne) wybiera walutę dla
   * jajka z `costOptions` (OR); pominięte → serwer bierze `cost` domyślny.
   * Dla jajek z `costAll` (AND) wyślij samo `eggDefId` — serwer pobierze wszystkie
   * wymagane waluty naraz.
   * Błędy: 400 insufficient_funds | egg_storage_full | biome_locked |
   *        unknown_egg | invalid_currency_option | event_inactive |
   *        validation_failed, 409 save_not_initialized.
   */
  /**
   * POST /eggs/exchange — wymiana `ratio` sztuk jednego rodzaju na 1 lepsze
   * z tego samego świata (E-6). `batches` = ile razy naraz.
   */
  async exchangeEggs(
    eggDefId: string,
    batches: number,
    /**
     * Klucz operacji — TEN SAM przy ponowieniu po błędzie sieci. Bez niego
     * utrata odpowiedzi po zapisie i ponowny klik zużywają drugą partię jajek
     * (wzorzec z `mergeBulk`).
     */
    operationId: string,
  ): Promise<{
    source_def_id: string;
    target_def_id: string;
    consumed: number;
    received: number;
    changes: EconomyDelta;
    save_rev: number;
  }> {
    return request(`/eggs/exchange`, {
      method: 'POST',
      json: { egg_def_id: eggDefId, batches, operation_id: operationId },
    });
  },

  async buyEgg(eggDefId: string, currency?: string): Promise<{ changes: EconomyDelta; save_rev: number }> {
    return request<{ changes: EconomyDelta; save_rev: number }>('/shop/buy-egg', {
      method: 'POST',
      json: currency ? { eggDefId, currency } : { eggDefId },
    });
  },

  /**
   * POST /events/:eventId/album/claim — odbierz nagrodę za komplet albumu (EV-3).
   * Zwraca deltę (gemy + profile z odblokowanymi awatarami + events z claimed).
   * Błędy: 400 album_incomplete | album_already_claimed | unknown_event_album |
   *        invalid_event_id, 409 save_not_initialized.
   */
  async claimEventAlbum(eventId: string): Promise<{ changes: EconomyDelta; save_rev: number }> {
    return request<{ changes: EconomyDelta; save_rev: number }>(
      `/events/${encodeURIComponent(eventId)}/album/claim`,
      { method: 'POST' },
    );
  },

  /** GET /events/:eventId/daily-trader/status — status dziennej paczki eventowej. */
  async getEventDailyTraderStatus(eventId: string): Promise<EventDailyTraderStatusResponse> {
    return request<EventDailyTraderStatusResponse>(
      `/events/${encodeURIComponent(eventId)}/daily-trader/status`,
      { method: 'GET' },
    );
  },

  /** POST /events/:eventId/daily-trader/claim — odbierz dzienną paczkę eventową. */
  async claimEventDailyTrader(eventId: string): Promise<EventDailyTraderClaimResponse> {
    return request<EventDailyTraderClaimResponse>(
      `/events/${encodeURIComponent(eventId)}/daily-trader/claim`,
      { method: 'POST' },
    );
  },

  /** POST /events/:eventId/pier-fishing/claim — mały połów muszelek z mola. */
  async claimEventPierFishing(eventId: string): Promise<EventPierFishingClaimResponse> {
    return request<EventPierFishingClaimResponse>(
      `/events/${encodeURIComponent(eventId)}/pier-fishing/claim`,
      { method: 'POST' },
    );
  },

  /**
   * POST /eggs/:uid/hatch — wykluj jajko. RNG liczy się NA SERWERZE.
   * Błędy: 404 egg_not_found, 400 pet_storage_full, 409 save_not_initialized.
   */
  async hatchEgg(
    uid: string,
  ): Promise<{ changes: EconomyDelta; hatch: HatchResultDTO; achievements_unlocked: AchievementUnlock[]; save_rev: number }> {
    return request<{ changes: EconomyDelta; hatch: HatchResultDTO; achievements_unlocked: AchievementUnlock[]; save_rev: number }>(
      `/eggs/${encodeURIComponent(uid)}/hatch`,
      { method: 'POST' },
    );
  },

  /**
   * POST /eggs/hatch-bulk — hurtowe wykluwanie 2-10 jajek tego samego defId
   * (5.D.3b). Aktywuje gwarancję `minRarityOnBulk` z configu jajka (np.
   * egg-legendary bulkSize=10 → min 1 epic w paczce).
   *
   * Błędy:
   *   - 400 validation_failed (uids out of range / duplikaty)
   *   - 400 mixed_egg_defs (różne defId w paczce)
   *   - 400 pet_storage_full (atomic — żadne jajko nie znika)
   *   - 404 egg_not_found (któryś uid nie istnieje)
   *   - 409 save_not_initialized
   */
  async hatchBulk(
    uids: string[],
  ): Promise<{
    changes: EconomyDelta;
    hatched: HatchBulkEntryDTO[];
    eggDefId: string;
    achievements_unlocked: AchievementUnlock[];
    save_rev: number;
  }> {
    return request<{
      changes: EconomyDelta;
      hatched: HatchBulkEntryDTO[];
      eggDefId: string;
      achievements_unlocked: AchievementUnlock[];
      save_rev: number;
    }>('/eggs/hatch-bulk', { method: 'POST', json: { uids } });
  },

  /**
   * POST /upgrades/:id/purchase — kup poziom ulepszenia.
   * Błędy: 400 insufficient_funds | max_level | unknown_upgrade.
   */
  async purchaseUpgrade(upgradeId: string): Promise<{ changes: EconomyDelta; save_rev: number }> {
    return request<{ changes: EconomyDelta; save_rev: number }>(
      `/upgrades/${encodeURIComponent(upgradeId)}/purchase`,
      { method: 'POST' },
    );
  },

  /**
   * POST /biomes/:id/unlock — odblokuj biom (waliduje coins + requiredPower).
   * NIE zmienia `currentBiomeId` — po sukcesie klient woła granularny `/enter`.
   * Błędy: 400 insufficient_funds | power_too_low | already_unlocked.
   */
  async unlockBiome(biomeId: string): Promise<{ changes: EconomyDelta; save_rev: number }> {
    return request<{ changes: EconomyDelta; save_rev: number }>(
      `/biomes/${encodeURIComponent(biomeId)}/unlock`,
      { method: 'POST' },
    );
  },

  /**
   * POST /biomes/:id/enter — ustaw aktualny, już odblokowany biom.
   * Zastępuje pełny `PUT /saves/me`, który na dużych zapisach był zawodny
   * szczególnie na telefonach. Błędy: 400 unknown_biome | biome_locked.
   */
  async enterBiome(biomeId: string): Promise<{ changes: EconomyDelta; save_rev: number }> {
    return request<{ changes: EconomyDelta; save_rev: number }>(
      `/biomes/${encodeURIComponent(biomeId)}/enter`,
      { method: 'POST' },
    );
  },

  /**
   * POST /starter-pets/choose — wybór petów startowych (onboarding krok 2).
   * Błędy: 400 invalid_starter_count | duplicate_starter_pet | invalid_starter_pet,
   *        409 starter_pets_already_chosen.
   */
  async chooseStarterPets(petDefIds: string[]): Promise<{ changes: EconomyDelta; save_rev: number }> {
    return request<{ changes: EconomyDelta; save_rev: number }>('/starter-pets/choose', {
      method: 'POST',
      json: { petDefIds },
    });
  },

  /**
   * POST /pets/:uid/equip — załóż (`equip:true`) / zdejmij (`false`) peta.
   * Idempotentny. Błędy: 404 pet_not_found, 400 equipped_cap_reached.
   */
  async equipPet(uid: string, equip: boolean): Promise<{ changes: EconomyDelta; save_rev: number }> {
    return request<{ changes: EconomyDelta; save_rev: number }>(
      `/pets/${encodeURIComponent(uid)}/equip`,
      { method: 'POST', json: { equip } },
    );
  },

  /**
   * POST /pets/:uid/flags — ustaw `locked` / `favorite` peta.
   * Błędy: 404 pet_not_found.
   */
  async setPetFlag(
    uid: string,
    flag: 'locked' | 'favorite',
    value: boolean,
  ): Promise<{ changes: EconomyDelta; save_rev: number }> {
    return request<{ changes: EconomyDelta; save_rev: number }>(
      `/pets/${encodeURIComponent(uid)}/flags`,
      { method: 'POST', json: { flag, value } },
    );
  },

  /**
   * POST /pets/merge — połącz pety (10 normal → gold, 12 gold → rainbow).
   * Błędy: 400 wrong_merge_count | pet_locked | pet_favorite | mixed_def |
   *        mixed_variant | unmergeable_variant | duplicate_pet_uid,
   *        404 pet_not_found.
   */
  async mergePets(
    petUids: string[],
  ): Promise<{ changes: EconomyDelta; merged: MergeResultDTO; gemsAwarded: number; save_rev: number }> {
    return request<{ changes: EconomyDelta; merged: MergeResultDTO; gemsAwarded: number; save_rev: number }>(
      '/pets/merge',
      {
        method: 'POST',
        json: { petUids },
      },
    );
  },

  /**
   * POST /pets/merge-bulk — atomowo wykonaj wiele partii/grup. Request nie
   * niesie UID-ów: serwer pod lockiem wybiera najstarsze wolne sztuki i chroni
   * favorite/locked/equipped/stall/job. `operationId` chroni retry po zgubionej
   * odpowiedzi. Błąd 409 `merge_plan_stale` oznacza zmianę dostępności, a
   * `merge_already_processed` — operację już zatwierdzoną.
   */
  async mergePetsBulk(
    operationId: string,
    groups: BulkMergeRequestGroup[],
  ): Promise<BulkMergeResponse> {
    return request<BulkMergeResponse>('/pets/merge-bulk', {
      method: 'POST',
      json: { operationId, groups },
    });
  },

  /**
   * POST /pets/:uid/sell-to-npc — sprzedaj peta do NPC za 💎 (Etap 5.0.E).
   * Cena server-authoritative (npcSellPrice z rarity+variant). Pet usuwany.
   * Błędy: 403 guest_cannot_trade, 409 pet_locked | pet_equipped,
   *        404 pet_not_found.
   */
  async sellPetToNpc(
    uid: string,
  ): Promise<{ changes: EconomyDelta; gemsReceived: number; gemsTotal: number; pet: { uid: string; rarity: string }; save_rev: number }> {
    return request<{ changes: EconomyDelta; gemsReceived: number; gemsTotal: number; pet: { uid: string; rarity: string }; save_rev: number }>(
      `/pets/${encodeURIComponent(uid)}/sell-to-npc`,
      { method: 'POST' },
    );
  },

  /**
   * POST /potions/buy — kup `qty` sztuk mikstury za 💎 (sink anty-inflacyjny).
   * Cena server-authoritative (SERVER_POTIONS). Błędy: 400 insufficient_funds |
   * unknown_potion | validation_failed, 409 save_not_initialized.
   */
  async buyPotion(
    potionDefId: string,
    qty: number,
  ): Promise<{ changes: EconomyDelta; gemsSpent: number; gemsRemaining: number; save_rev: number }> {
    return request<{ changes: EconomyDelta; gemsSpent: number; gemsRemaining: number; save_rev: number }>(
      '/potions/buy',
      { method: 'POST', json: { potionDefId, qty } },
    );
  },

  /**
   * POST /potions/use — użyj 1 sztuki mikstury → aktywuje buff czasowy.
   * `expiresAt` ustawiany zegarem serwera. Błędy: 400 no_potion |
   * unknown_potion, 409 save_not_initialized.
   */
  async usePotion(
    potionDefId: string,
  ): Promise<{ changes: EconomyDelta; save_rev: number }> {
    return request<{ changes: EconomyDelta; save_rev: number }>(
      '/potions/use',
      { method: 'POST', json: { potionDefId } },
    );
  },

  /**
   * POST /world/tick — kredytuje monety zebrane w świecie + zniszczone obiekty
   * (sesja 4.D.3). Serwer DODAJE deltę (append-only) i zwraca TYLKO skalary
   * `{ currencies, stats, gemsRolled }` (Faza 1a dieta payloadu — NIE cały save;
   * ekwipunek pet/jajka zostaje u klienta). Wołane przez `AutoSaveBridge` co ~3-10s.
   *
   * `tickId` — idempotency key (4.D.3 FIXES): bridge retry'uje TEN SAM tickId
   * po błędzie; serwer dedupuje przez tabelę `world_ticks` (append-only
   * endpoint — bez tego retry po commitcie podwoiłby monety). Błędy: 409
   * save_not_initialized.
   */
  async worldTick(
    tickId: string,
    coinsEarned: string,
    objectsDestroyed: number,
    // Etap 5, 5.0.B: opcjonalny breakdown destruktibli per typ (do server
    // gem roll). Backward compat — stari klienci bez tego pola nadal działają
    // (po prostu nie dostają gems, schema backendu ma `.optional()`).
    destroyedByType?: Record<string, number>,
  ): Promise<WorldTickResult> {
    return request<WorldTickResult>('/world/tick', {
      method: 'POST',
      json: {
        tickId,
        coinsEarned,
        objectsDestroyed,
        ...(destroyedByType && Object.keys(destroyedByType).length > 0
          ? { destroyedByType }
          : {}),
      },
    });
  },
};

// ── Atlantis World Boss „Strażnik Świątyni" (ATL-2b) ────────────────────────

export type BossState = 'dormant_cooling' | 'dormant_ready' | 'awake';

/** Odroczona nagroda (jajka/pet czekają na zrobienie miejsca). */
export interface BossPendingReward {
  royalEggId: string;
  royalEggs: number;
  rulerPetId: string | null;
}

/** Stan bossa (GET /biomes/atlantis/boss). Liczby HP to stringi (BigInt). */
export interface BossView {
  state: BossState;
  hpRemaining: string;
  hpMax: string;
  awakenedUntil: number | null;
  nextAvailableAt: number | null;
  totalDamage: string;
  firstClearDone: boolean;
  shielded: boolean;
  phaseHpRemaining: string;
  phaseIndex: number;
  pendingReward: BossPendingReward | null;
}

/** Nagroda za pokonanie (gdy defeated). */
export interface BossReward {
  coins: string;
  gems: number;
  royalEggId: string;
  royalEggs: number;
  rulerPetId: string | null;
  firstClear: boolean;
  title: string | null;
}

/** Odpowiedź /boss/hit. `save_rev` obecny tylko przy kredycie (defeat/pocieszenie);
 *  typowany jako number dla `runEconomyAction` (czytany wyłącznie gdy `changes`). */
export interface BossHitResponse extends BossView {
  applied: string;
  defeated: boolean;
  phase: number | null;
  reward: BossReward | null;
  firstClear: boolean;
  rewardDeferred?: boolean;
  consolationGems?: number;
  changes?: EconomyDelta;
  save_rev: number;
}

export interface BossClaimResponse {
  claimed: { royalEggs: number; rulerPetId: string | null };
  changes?: EconomyDelta;
  save_rev: number;
}

export const bossApi = {
  /** GET stan bossa (lazy-reconcile po stronie serwera). */
  async getState(): Promise<BossView> {
    return request<BossView>('/biomes/atlantis/boss', { method: 'GET' });
  },
  /**
   * POST uderzenie. `damage` = duży string (serwer clampuje do budżetu
   * `elapsed × dps_cap` — klient nie zna krzywej). `hitId` = UUID per request
   * (idempotencja). Błędy: 409 dormant_cooling / make_room / pending_reward_unclaimed.
   */
  async hit(damage: string, hitId: string): Promise<BossHitResponse> {
    return request<BossHitResponse>('/biomes/atlantis/boss/hit', {
      method: 'POST',
      json: { damage, hitId },
    });
  },
  /** POST odbiór odroczonej nagrody (po zrobieniu miejsca). */
  async claimPending(): Promise<BossClaimResponse> {
    return request<BossClaimResponse>('/biomes/atlantis/boss/claim-pending', { method: 'POST' });
  },
};

// ── Market price guide / RAP-like ───────────────────────────────────────────

/**
 * Przewodnik cenowy JAJEK (E-4). Bez wariantu (jajko ma tylko rodzaj) i bez
 * ceny skupu NPC (jajek NPC nie skupuje — świadoma decyzja, żeby nie tworzyć
 * drukarki kryształów). Za to z KOTWICĄ: gdy rynek jeszcze nic nie wie,
 * podpowiedź bierze się z tabeli klasa × tier biomu.
 */
export interface EggMarketPriceGuideDTO {
  def_id: string;
  recent_average_crystals: number | null;
  suggested_price_crystals: number | null;
  /** `market` = z WIARYGODNEJ historii, `anchor` = z tabeli, `none` = brak podpowiedzi. */
  suggestion_source: 'market' | 'anchor' | 'none';
  /**
   * Czy próbka jest dość liczna I niezależna, żeby na niej polegać. Jedna
   * sprzedaż jednej pary kont NIE jest rynkiem — bez tego progu para graczy
   * ustawiłaby cenę odniesienia dla wszystkich (Codex E-4 MEDIUM 1).
   */
  market_trusted: boolean;
  trust_min_samples: number;
  trust_min_pairs: number;
  anchor_price_crystals: number | null;
  typical_low_crystals: number | null;
  typical_high_crystals: number | null;
  sample_size: number;
  used_sample_size: number;
  removed_outliers: number;
  confidence: 'none' | 'low' | 'medium' | 'high';
  unique_pair_count: number;
  max_sales_per_pair: number;
  sample_limit: number;
  window_days: number;
  last_sold_at: string | null;
  lowest_active_listing_crystals: number | null;
  recent_sales: Array<{
    price_crystals: number;
    sold_at: string;
    included_in_average: boolean;
  }>;
}

export interface PetMarketPriceGuideDTO {
  def_id: string;
  variant: Variant;
  recent_average_crystals: number | null;
  suggested_price_crystals: number | null;
  typical_low_crystals: number | null;
  typical_high_crystals: number | null;
  sample_size: number;
  used_sample_size: number;
  removed_outliers: number;
  confidence: 'none' | 'low' | 'medium' | 'high';
  unique_pair_count: number;
  max_sales_per_pair: number;
  sample_limit: number;
  window_days: number;
  last_sold_at: string | null;
  npc_sell_floor_crystals: number;
  lowest_active_listing_crystals: number | null;
  recent_sales: Array<{
    price_crystals: number;
    sold_at: string;
    included_in_average: boolean;
  }>;
}

export const marketApi = {
  /**
   * Średnia i historia faktycznych sprzedaży dla dokładnego typu + wariantu.
   * Direct trade nie jest źródłem ceny, bo jedna wymiana może mieć wiele petów.
   */
  async petPriceGuide(defId: string, variant: Variant): Promise<PetMarketPriceGuideDTO> {
    return request<PetMarketPriceGuideDTO>(
      `/market/pets/${encodeURIComponent(defId)}/${encodeURIComponent(variant)}/price-guide`,
      { method: 'GET' },
    );
  },

  /**
   * To samo dla stosu jajek (E-4). Cena liczona z `unit_price` sprzedaży, nie
   * z kwoty całego zakupu — inaczej kupno 5 sztuk po 10 zawyżałoby średnią do 50.
   */
  async eggPriceGuide(eggDefId: string): Promise<EggMarketPriceGuideDTO> {
    return request<EggMarketPriceGuideDTO>(
      `/market/eggs/${encodeURIComponent(eggDefId)}/price-guide`,
      { method: 'GET' },
    );
  },

  /**
   * Przewodniki dla WIELU rodzajów jednym żądaniem. Widok kupującego pokazuje
   * naraz oferty wielu rodzajów — osobne żądanie na każdy znaczyłoby przy
   * zimnym otwarciu rynku kilkadziesiąt równoległych zapytań.
   */
  async eggPriceGuides(eggDefIds: string[]): Promise<Record<string, EggMarketPriceGuideDTO>> {
    if (eggDefIds.length === 0) return {};
    const res = await request<{ guides: Record<string, EggMarketPriceGuideDTO> }>(
      `/market/eggs/price-guide?def_ids=${encodeURIComponent(eggDefIds.join(','))}`,
      { method: 'GET' },
    );
    return res.guides;
  },
};

// ── Stalls / Zagrody (Etap 5.A) ─────────────────────────────────────────────

/** Listing peta w zagrodzie. */
export interface StallListingDTO {
  id: string;
  pet_uid: string;
  price_crystals: number;
  listed_at?: string;
}

/**
 * Zagroda BEZ listingów — zwracana przez rent/extend (Codex 5.A F1 LOW 1:
 * backend rent/extend nie zwraca `listings`, więc typ tego nie obiecuje).
 */
export interface StallSummaryDTO {
  id: string;
  level: number;
  slots: number;
  instance_id: number;
  rented_until: string;
}

/** Zagroda Z listingami — zwracana przez /stalls/mine. */
/** Stos jajek wystawiony w budce (E-2). Cena jest ZA SZTUKĘ, nie za stos. */
export interface StallEggListingDTO {
  id: string;
  egg_def_id: string;
  quantity: number;
  unit_price_crystals: number;
  listed_at: string;
}

export interface StallDTO extends StallSummaryDTO {
  listings: StallListingDTO[];
  /**
   * Stosy jajek. Opcjonalne, bo starszy backend (przed E-2) tego pola nie zwraca
   * — klient musi przeżyć rozjazd wersji między kontenerem web a api.
   */
  egg_listings?: StallEggListingDTO[];
}

/** Listing w przeglądzie plazy — wzbogacony o dane peta (CO kupujesz). */
export interface BrowseListingDTO {
  id: string;
  pet_uid: string;
  price_crystals: number;
  pet: { def_id: string; variant: string; power: number } | null;
}

/** Zagroda w przeglądzie instancji — z nickiem właściciela + wzbogaconymi listingami. */
export interface BrowseStallDTO {
  id: string;
  level: number;
  slots: number;
  instance_id: number;
  owner_display_name: string;
  rented_until: string;
  listings: BrowseListingDTO[];
  /**
   * Stosy jajek w tej budce (E-3). Opcjonalne — starszy backend tego pola nie
   * zwraca, a kontenery web i api bywają przebudowywane osobno.
   */
  egg_listings?: StallEggListingDTO[];
}

export const stallApi = {
  async sales(cursor?: string): Promise<{ records: StallSale[]; next_cursor: string | null }> {
    return request(`/trade-history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`, { method: 'GET' });
  },
  /** GET /stalls/mine — moje aktywne zagrody + listingi (lazy-expire). */
  async mine(): Promise<{ stalls: StallDTO[] }> {
    return request<{ stalls: StallDTO[] }>('/stalls/mine', { method: 'GET' });
  },

  /** POST /stalls/rent — wynajmij zagrodę. Zwraca { stall, changes, achievements_unlocked }. */
  async rent(
    level: number,
    days?: number,
  ): Promise<{ stall: StallSummaryDTO; changes: EconomyDelta; achievements_unlocked: AchievementUnlock[]; save_rev: number }> {
    return request<{ stall: StallSummaryDTO; changes: EconomyDelta; achievements_unlocked: AchievementUnlock[]; save_rev: number }>(
      '/stalls/rent',
      { method: 'POST', json: { level, ...(days ? { days } : {}) } },
    );
  },

  /** POST /stalls/:id/list-pet — wystaw peta. Zwraca { listing, changes, achievements_unlocked }. */
  async listPet(
    stallId: string,
    petUid: string,
    priceCrystals: number,
  ): Promise<{ listing: StallListingDTO; changes: EconomyDelta; achievements_unlocked: AchievementUnlock[]; save_rev: number }> {
    return request<{ listing: StallListingDTO; changes: EconomyDelta; achievements_unlocked: AchievementUnlock[]; save_rev: number }>(
      `/stalls/${encodeURIComponent(stallId)}/list-pet`,
      { method: 'POST', json: { pet_uid: petUid, price_crystals: priceCrystals } },
    );
  },

  /** POST /stalls/:id/withdraw/:listing_id — zdejmij listing. Zwraca { changes }. */
  async withdraw(stallId: string, listingId: string): Promise<{ changes: EconomyDelta; save_rev: number }> {
    return request<{ changes: EconomyDelta; save_rev: number }>(
      `/stalls/${encodeURIComponent(stallId)}/withdraw/${encodeURIComponent(listingId)}`,
      { method: 'POST' },
    );
  },

  /**
   * POST /stalls/:id/list-eggs — wystaw STOS jajek (E-2).
   *
   * Powtórne wystawienie tego samego rodzaju DOKŁADA do istniejącego stosu
   * i nadpisuje cenę całego stosu — dlatego UI musi o tym uprzedzić.
   */
  async listEggs(
    stallId: string,
    eggDefId: string,
    quantity: number,
    unitPriceCrystals: number,
  ): Promise<{ listing: StallEggListingDTO; changes: EconomyDelta; save_rev: number }> {
    return request<{ listing: StallEggListingDTO; changes: EconomyDelta; save_rev: number }>(
      `/stalls/${encodeURIComponent(stallId)}/list-eggs`,
      {
        method: 'POST',
        json: { egg_def_id: eggDefId, quantity, unit_price_crystals: unitPriceCrystals },
      },
    );
  },

  /**
   * POST /stalls/:id/withdraw-eggs/:listing_id — zdejmij sztuki ze stosu (E-2).
   * Bez `quantity` = cały stos.
   */
  async withdrawEggs(
    stallId: string,
    listingId: string,
    quantity?: number,
  ): Promise<{ freed: number; remaining: number; changes: EconomyDelta; save_rev: number }> {
    return request<{ freed: number; remaining: number; changes: EconomyDelta; save_rev: number }>(
      `/stalls/${encodeURIComponent(stallId)}/withdraw-eggs/${encodeURIComponent(listingId)}`,
      { method: 'POST', json: quantity ? { quantity } : {} },
    );
  },

  /**
   * POST /stalls/:id/buy-eggs/:listing_id — kup N sztuk ze stosu (E-3).
   *
   * `unitPriceCrystals` to ZGODA NA CENĘ przepisana z ekranu: stos można
   * przecenić, więc serwer odrzuca zakup (`price_changed`), jeśli cena zmieniła
   * się między wyświetleniem a kliknięciem — zamiast po cichu pobrać więcej.
   */
  async buyEggs(
    stallId: string,
    listingId: string,
    quantity: number,
    unitPriceCrystals: number,
  ): Promise<{
    eggs: Array<{ uid: string; def_id: string }>;
    quantity: number;
    gems_paid: number;
    remaining: number;
    changes: EconomyDelta;
    save_rev: number;
    achievements_unlocked: AchievementUnlock[];
  }> {
    return request(
      `/stalls/${encodeURIComponent(stallId)}/buy-eggs/${encodeURIComponent(listingId)}`,
      { method: 'POST', json: { quantity, unit_price_crystals: unitPriceCrystals } },
    );
  },

  /** POST /stalls/:id/extend — przedłuż najem o N dni. Zwraca { stall, changes }. */
  async extend(stallId: string, days: number): Promise<{ stall: StallSummaryDTO; changes: EconomyDelta; save_rev: number }> {
    return request<{ stall: StallSummaryDTO; changes: EconomyDelta; save_rev: number }>(
      `/stalls/${encodeURIComponent(stallId)}/extend`,
      { method: 'POST', json: { days } },
    );
  },

  /** GET /stalls/by-instance/:id — przegląd aktywnych zagród w instancji (F2). */
  async byInstance(instanceId: number): Promise<{ instance_id: number; stalls: BrowseStallDTO[] }> {
    return request<{ instance_id: number; stalls: BrowseStallDTO[] }>(
      `/stalls/by-instance/${instanceId}`,
      { method: 'GET' },
    );
  },

  /** POST /stalls/:id/buy/:listing_id — kup peta. Zwraca { pet, gems_paid, changes, achievements_unlocked }. */
  async buy(
    stallId: string,
    listingId: string,
  ): Promise<{
    pet: { uid: string; def_id: string; variant: string };
    gems_paid: number;
    changes: EconomyDelta;
    achievements_unlocked: AchievementUnlock[];
    save_rev: number;
  }> {
    return request<{
      pet: { uid: string; def_id: string; variant: string };
      gems_paid: number;
      changes: EconomyDelta;
      achievements_unlocked: AchievementUnlock[];
      save_rev: number;
    }>(`/stalls/${encodeURIComponent(stallId)}/buy/${encodeURIComponent(listingId)}`, { method: 'POST' });
  },
};

// ── Leaderboard (sesja 4.F.2 / gemy) ────────────────────────────────────────

/**
 * Metryka rankingu: moc drużyny, liczba gemów, złota (coins) albo liczba
 * specjalnych petów (wariant ≠ normal).
 */
export type LeaderboardMetric = 'power' | 'gems' | 'coins' | 'variants';

/** Jeden wiersz rankingu. */
export interface LeaderboardEntry {
  rank: number;
  /** Wyświetlana nazwa (ładny nick gościa albo display_name/username konta). */
  name: string;
  /** Czy to wiersz gracza pytającego (do podświetlenia „(Ty)"). */
  isMe: boolean;
  /** Wartość aktywnej metryki (moc / gemy / złoto / liczba specjalnych petów). */
  value: number | string;
}

/** Odpowiedź `GET /leaderboard` — TOP N + pozycja gracza pytającego. */
export interface LeaderboardResponse {
  /** Echo metryki, dla której policzono ranking. */
  metric: LeaderboardMetric;
  leaderboard: LeaderboardEntry[];
  me: { rank: number; value: number | string };
}

export const leaderboardApi = {
  /**
   * GET /leaderboard?metric — ranking wg wybranej metryki + własna pozycja.
   * `power` (domyślnie) = moc drużyny, `gems` = gemy, `coins` = złoto,
   * `variants` = liczba specjalnych petów (wariant ≠ normal).
   */
  async get(metric: LeaderboardMetric = 'power'): Promise<LeaderboardResponse> {
    return request<LeaderboardResponse>(`/leaderboard?metric=${metric}`);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Config API — Trading Plaza (Etap 5, 5.0.0b)
// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth dla UI: fetcha config z serwera zamiast importować
// `TRADING_PLAZA` bezpośrednio. Dzięki temu zmiana ceny w `balance.config.ts`
// + restart backendu → klient widzi nowe wartości (po max 5 min cache).
//
// Patrz DESIGN_TRADING_PLAZA.md §8.5 Reguła 3.

import type { TradingPlazaConfig } from '@/config/balance.config';
import type { BankConfig } from '@/config/bank.types';

/**
 * Response z `GET /config/trading-plaza`. `version` używamy do audit
 * (forward-compat: gdy zmienimy ceny, historia trade'ów zapisze wersję).
 */
export interface TradingPlazaConfigResponse {
  version: string;          // np. 'v1'
  config: TradingPlazaConfig;
}

/** Response z `GET /config/bank` (BANK-0) — ten sam kształt co trading-plaza. */
export interface BankConfigResponse {
  version: string;          // np. 'v1'
  config: BankConfig;
}

export const configApi = {
  /**
   * GET /config/trading-plaza — pełny config (zagrody, ceny, limity, etc.).
   * PUBLICZNY — gość też dostaje, bo musi widzieć ceny w UI przed claim.
   * Cache po stronie serwera (5 min) + Zustand cache po stronie klienta.
   */
  async getTradingPlaza(): Promise<TradingPlazaConfigResponse> {
    return request<TradingPlazaConfigResponse>('/config/trading-plaza');
  },

  /**
   * GET /config/bank — pełny config Banku (BANK-0: progi naklejek, stawki
   * pracy, oprocentowanie lokat). PUBLICZNY — gość widzi album read-only (B6).
   */
  async getBank(): Promise<BankConfigResponse> {
    return request<BankConfigResponse>('/config/bank');
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Bank API — album naklejek (BANK-A1/A2), praca i lokaty dojdą w BANK-B/C
// ─────────────────────────────────────────────────────────────────────────────

/** Stan jednej naklejki w albumie banku (`GET /bank/state`). */
export type BankPhotoVariant = Variant;

export interface BankStickerState {
  /** Postęp albumu; fotografia chronionego wariantu zwraca co najmniej wymagany komplet. */
  count: number;
  completedAt: string | null;
  /** Ustawione, gdy wpis ukończyła niedestrukcyjna sesja zdjęciowa. */
  photographedAt?: string | null;
  photographedVariant?: BankPhotoVariant | null;
}

/**
 * Pet w pracy w kopalni banku (BANK-B). Zarobek „uzbierane ~X" klient liczy
 * LOKALNIE z `lastCollectedAt` + stawek z `bankConfig` (podgląd); wypłata
 * autorytatywnie przy collect/recall (zegar serwera).
 */
export interface BankJobState {
  petUid: string;
  job: 'coins' | 'gems';
  startedAt: string;
  lastCollectedAt: string;
}

/**
 * Aktywna lokata (BANK-C). `rateBps` = snapshot oprocentowania z chwili
 * założenia; `maturesAt` = zegar serwera. Zamknięte lokaty nie lecą do klienta.
 */
export interface BankDepositState {
  id: string;
  currency: 'coins' | 'gems';
  amount: number;
  rateBps: number;
  termDays: number;
  maturesAt: string;
  createdAt: string;
}

/** GET /bank/state — album + prace + lokaty, w jednym requeście. */
export interface BankStateResponse {
  /** Zegar serwera do bezpiecznego wyznaczenia dojrzałości lokat. */
  serverNow: string;
  album: Record<string, BankStickerState>;
  jobs: BankJobState[];
  /** Pety odrzucane przez paste (lock/equip/favorite/stall/job) — do dokładnego badge „GOTOWE!". */
  albumBlockedPetUids?: string[];
  deposits: BankDepositState[];
  /** Skarbiec: SAM licznik (zawartość przez stronicowany `GET /bank/vault`). */
  /** Skarbiec: ile petów schowanych i ile się mieści (sufit z `vault_storage`). */
  vault: { petCount: number; cap: number };
}

export type BankAlbumPetSource = 'backpack' | 'vault';

/** Lekki indeks skarbca: agregaty wpisów albumu, bez UID-ów i pełnej listy. */
export interface BankAlbumVaultIndexEntry {
  ownedCount: number;
  pasteableNormalCount: number;
  photoVariants: BankPhotoVariant[];
}

export interface BankAlbumVaultIndexResponse {
  entries: Record<string, BankAlbumVaultIndexEntry>;
}

/** Jeden z maksymalnie kilku petów wybranego gatunku, które panel może użyć. */
export interface BankAlbumVaultCandidate {
  uid: string;
  defId: string;
  variant: BankPhotoVariant;
  locked: boolean;
  favorite: boolean;
  exp: number;
  acquiredAt: number;
  source: 'vault';
}

export interface BankAlbumCandidatesResponse {
  defId: string;
  ownedCount: number;
  pasteableCount: number;
  candidates: BankAlbumVaultCandidate[];
}

/** POST /bank/album/paste — delta + stan naklejki po wklejeniu. */
export interface BankPasteResponse {
  changes: EconomyDelta;
  save_rev: number;
  /** `completedAt` = znacznik SERWERA (klient nie syntetyzuje własnym zegarem). */
  sticker: BankStickerState & { defId: string; required: number; completed: boolean };
  gemsAwarded: number;
  pageBonusAwarded: number;
  source: BankAlbumPetSource;
  /** `null` dla peta z plecaka; po użyciu skarbca autorytatywny nowy licznik. */
  vaultPetCount: number | null;
}

/** POST /bank/album/photo — pet zostaje, a wszystkie wymagane naklejki są zaliczone. */
export interface BankPhotoResponse {
  changes: EconomyDelta;
  save_rev: number;
  sticker: BankStickerState & { defId: string; required: number; completed: true };
  variant: BankPhotoVariant;
  petPreserved: true;
  /** True tylko przy bezpiecznym retry już wykonanej fotografii. */
  alreadyPhotographed: boolean;
  source: BankAlbumPetSource;
  gemsAwarded: number;
  pageBonusAwarded: number;
}


/** Sortowanie listingu skarbca (BANK-D). Zamknięta lista — patrz §5.1 designu. */
export type VaultSort = 'power' | 'recent';

/** Pet w skarbcu. `power` jako STRING — moc bywa większa niż `Number.MAX_SAFE_INTEGER`. */
export interface VaultPet {
  uid: string;
  defId: string;
  variant: string;
  power: string;
  locked: boolean;
  favorite: boolean;
  exp: number;
  acquiredAt: number;
}

/** GET /bank/vault — jedna strona + kursor do następnej + licznik całości. */
export interface VaultListResponse {
  /** Sufit skarbca (V-5) — okno pokazuje „N / M" bez osobnego zapytania. */
  cap: number;
  pets: VaultPet[];
  /** `null` = ostatnia strona. */
  nextCursor: string | null;
  /** Cały skarbiec, nie ta strona — z licznika `bank_vault`, nie z `COUNT(*)`. */
  total: number;
}

/** Filtr hurtowego chowania. Wszystkie pola opcjonalne, łączone przez AND. */
export interface VaultPreviewResponse {
  dryRun: true;
  /** Ile petów pasuje do filtru. */
  count: number;
  /** Ile już leży w skarbcu. */
  vaultCount: number;
  /** Sufit skarbca przy obecnym poziomie `vault_storage`. */
  cap: number;
}

export interface VaultBulkFilter {
  biomeId?: string;
  rarity?: string;
  variant?: string;
  /** „Schowaj wszystko słabsze lub równe X" — moc jako string. */
  maxPower?: string;
}

/** Odpowiedź przenosin: delta dla store'a + stan skarbca. */
export interface VaultMoveResponse {
  changes: EconomyDelta;
  save_rev: number;
  moved: number;
  total: number;
}

export const bankApi = {
  /**
   * GET /bank/state — album (defId → {count, completedAt}). Gość TEŻ dostaje
   * (album read-only, B6 — dla gościa pusty).
   */
  async state(): Promise<BankStateResponse> {
    return request<BankStateResponse>('/bank/state');
  },

  /** Agregaty potrzebne do odkrycia slotów, bez pobierania zawartości skarbca. */
  async albumVaultIndex(): Promise<BankAlbumVaultIndexResponse> {
    return request<BankAlbumVaultIndexResponse>('/bank/album/vault-index');
  },

  /** Mała lista kandydatów tylko dla aktualnie otwartego wpisu albumu. */
  async albumCandidates(defId: string): Promise<BankAlbumCandidatesResponse> {
    return request<BankAlbumCandidatesResponse>(`/bank/album/candidates/${encodeURIComponent(defId)}`);
  },

  /**
   * POST /bank/album/paste — wklej zwykłą naklejkę. ZUŻYWA peta (pet-sink B2)!
   * Mythic oraz gold/rainbow/huge/titanic są odrzucane (`pet_requires_photo`)
   * i mają osobny, niedestrukcyjny endpoint.
   * Wołać przez `runEconomyAction` (delta `changes.removedPetUid` + currencies).
   * Błędy: 403 guest_bank_locked, 404 pet_not_found, 409 pet_locked |
   * pet_equipped | pet_favorite | pet_in_stall | pet_in_job | pet_requires_photo |
   * vault_counter_conflict |
   * pet_not_in_album | save_not_initialized.
   */
  async pasteSticker(petUid: string): Promise<BankPasteResponse> {
    return request<BankPasteResponse>('/bank/album/paste', {
      method: 'POST',
      json: { petUid },
    });
  },

  /**
   * POST /bank/album/photo — sesja dla mythic i wariantów scalanych/premium.
   * Pet NIE znika.
   * Endpoint jest idempotentny per konto+defId, więc retry nie dubluje bonusu.
   */
  async photographSticker(petUid: string): Promise<BankPhotoResponse> {
    return request<BankPhotoResponse>('/bank/album/photo', {
      method: 'POST',
      json: { petUid },
    });
  },

  /**
   * POST /bank/jobs/assign — wyślij peta do kopalni (BANK-B). Wołać przez
   * `runEconomyAction`. Błędy: 403 guest_bank_locked, 404 pet_not_found,
   * 409 work_slots_full | pet_in_job | pet_equipped | pet_in_stall.
   */
  async assignJob(
    petUid: string,
    job: 'coins' | 'gems',
  ): Promise<{ changes: EconomyDelta; save_rev: number; job: BankJobState; slotsUsed: number; slotsMax: number }> {
    return request<{ changes: EconomyDelta; save_rev: number; job: BankJobState; slotsUsed: number; slotsMax: number }>(
      '/bank/jobs/assign',
      { method: 'POST', json: { petUid, job } },
    );
  },

  /**
   * POST /bank/jobs/collect — odbierz wypłatę ze WSZYSTKICH prac naraz.
   * Wołać przez `runEconomyAction` (delta currencies). Prace, które nic nie
   * uzbierały (np. 💎 poniżej pełnej godziny), NIE tracą postępu.
   */
  async collectJobs(): Promise<{ changes: EconomyDelta; save_rev: number; coinsAdded: string; gemsAdded: number; jobsPaid: number }> {
    return request<{ changes: EconomyDelta; save_rev: number; coinsAdded: string; gemsAdded: number; jobsPaid: number }>(
      '/bank/jobs/collect',
      { method: 'POST' },
    );
  },

  /**
   * POST /bank/jobs/recall — odwołaj peta z pracy (wypłata zaległości +
   * zwolnienie od razu). Wołać przez `runEconomyAction`.
   */
  async recallJob(
    petUid: string,
  ): Promise<{ changes: EconomyDelta; save_rev: number; coinsAdded: string; gemsAdded: number }> {
    return request<{ changes: EconomyDelta; save_rev: number; coinsAdded: string; gemsAdded: number }>(
      '/bank/jobs/recall',
      { method: 'POST', json: { petUid } },
    );
  },

  /**
   * POST /bank/deposits — załóż lokatę (BANK-C). Waluta schodzi od razu.
   * Wołać przez `runEconomyAction`. Błędy: 403 guest_bank_locked,
   * 400 unknown_term | amount_too_small | amount_too_large | insufficient_funds,
   * 409 deposit_exists (max 1 aktywna/walutę).
   */
  async openDeposit(
    currency: 'coins' | 'gems',
    amount: number,
    termDays: number,
  ): Promise<{ changes: EconomyDelta; save_rev: number; deposit: BankDepositState }> {
    return request<{ changes: EconomyDelta; save_rev: number; deposit: BankDepositState }>(
      '/bank/deposits',
      { method: 'POST', json: { currency, amount, termDays } },
    );
  },

  /**
   * POST /bank/deposits/:id/withdraw — odbierz (po terminie: kapitał+odsetki)
   * albo zerwij (przed: sam kapitał, ZERO KAR — B7). Wołać przez
   * `runEconomyAction`. Błędy: 404 deposit_not_found, 409 deposit_already_withdrawn.
   */
  async withdrawDeposit(
    id: string,
  ): Promise<{ changes: EconomyDelta; save_rev: number; principal: string; interest: string; matured: boolean; currency: 'coins' | 'gems' }> {
    return request<{ changes: EconomyDelta; save_rev: number; principal: string; interest: string; matured: boolean; currency: 'coins' | 'gems' }>(
      `/bank/deposits/${encodeURIComponent(id)}/withdraw`,
      { method: 'POST' },
    );
  },

  // ── Skarbiec (BANK-D) ──────────────────────────────────────────────────────
  //
  // UWAGA NA KSZTAŁT: pety skarbca NIE są częścią `PlayerSave` i NIE trafiają
  // do store'a gry. Listing jest stronicowany i żyje w stanie okna skarbca,
  // bo skarbiec ma mieścić dziesiątki tysięcy petów — wciągnięcie ich do
  // globalnego stanu zniweczyłoby cały sens modułu (patrz DESIGN_BANK_VAULT.md §2).

  /**
   * GET /bank/vault — jedna strona skarbca.
   *
   * `cursor` jest NIEPRZEZROCZYSTY: klient przekazuje to, co dostał w
   * `nextCursor`, i nigdy go sam nie konstruuje. Token niesie sort i filtr,
   * więc po ich zmianie trzeba zacząć od `cursor: undefined` — inaczej serwer
   * odpowie `400 invalid_cursor`.
   */
  async vaultList(params: {
    cursor?: string;
    limit?: number;
    sort?: VaultSort;
    query?: string;
    origin?: string;
    rarity?: string;
    variant?: string;
  } = {}): Promise<VaultListResponse> {
    const q = new URLSearchParams();
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.sort) q.set('sort', params.sort);
    if (params.query) q.set('query', params.query);
    if (params.origin) q.set('origin', params.origin);
    if (params.rarity) q.set('rarity', params.rarity);
    if (params.variant) q.set('variant', params.variant);
    const qs = q.toString();
    return request<VaultListResponse>(`/bank/vault${qs ? `?${qs}` : ''}`);
  },

  /**
   * POST /bank/vault/deposit — schowaj wskazane pety (max 100 na wywołanie).
   * Wołać przez `runEconomyAction`: delta niesie `removedPetUids`, więc pety
   * znikają z lokalnego plecaka bez przeładowania gry.
   */
  async vaultDeposit(petUids: string[]): Promise<VaultMoveResponse> {
    return request<VaultMoveResponse>('/bank/vault/deposit', {
      method: 'POST',
      json: { petUids },
    });
  },

  /**
   * POST /bank/vault/withdraw — wyjmij pety do plecaka. Wymaga w nim miejsca
   * (sufit `petStorageCap`), inaczej `409 pet_storage_full`. Delta niesie
   * `newPets` w pełnym kształcie DTO.
   */
  async vaultWithdraw(petUids: string[]): Promise<VaultMoveResponse> {
    return request<VaultMoveResponse>('/bank/vault/withdraw', {
      method: 'POST',
      json: { petUids },
    });
  },

  /**
   * Podgląd hurtu — ILE petów wpadnie pod filtr. NIC nie zmienia.
   *
   * Serwer liczy to DOKŁADNIE tym samym warunkiem co realne wykonanie, więc
   * liczba pokazana graczowi nie może rozminąć się z tym, co faktycznie zniknie.
   */
  /**
   * Podgląd hurtu. `vaultCount`/`cap` pozwalają UI ostrzec „nie zmieści się"
   * ZANIM gracz kliknie — serwer i tak odrzuci taki hurt (`409 vault_full`),
   * ale pokazanie tego dopiero po kliknięciu byłoby złą informacją zwrotną.
   */
  async vaultBulkPreview(filter: VaultBulkFilter): Promise<VaultPreviewResponse> {
    return request<VaultPreviewResponse>('/bank/vault/deposit-bulk', {
      method: 'POST',
      json: { filter, dryRun: true },
    });
  },

  /**
   * POST /bank/vault/deposit-bulk — schowaj wszystko, co pasuje do filtru.
   *
   * `expectedCount` jest WYMAGANY i musi pochodzić z `vaultBulkPreview`.
   * Gdy stan zmieni się między podglądem a wykonaniem, serwer odpowie
   * `409 vault_plan_stale` — wtedy trzeba odświeżyć podgląd i zapytać gracza
   * ponownie, NIGDY nie ponawiać po cichu. Ten sam błąd dostaniemy, jeśli
   * pierwsza odpowiedź zginęła w sieci, a operacja się wykonała: to sygnał
   * „zrób resync", nie „spróbuj jeszcze raz".
   */
  async vaultBulkDeposit(
    filter: VaultBulkFilter,
    expectedCount: number,
  ): Promise<VaultMoveResponse> {
    return request<VaultMoveResponse>('/bank/vault/deposit-bulk', {
      method: 'POST',
      json: { filter, expectedCount },
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Rewards API — daily reward (Etap 5, 5.0.C)
// ─────────────────────────────────────────────────────────────────────────────

/** GET /rewards/daily-status response — czy claim available + meta. */
export interface DailyStatusResponse {
  can_claim: boolean;
  last_claim_at: string | null;
  next_claim_at: string | null;
  streak_days: number;
  crystals_available: number;
}

/** POST /rewards/daily-claim response — sukces. */
export interface DailyClaimResponse {
  gems_received: number;
  /**
   * Autorytatywne `save.gems` po claim (5.0.C FIXES Codex HIGH 1).
   * Frontend MUSI używać tego, NIE `gems + gems_received` lokalnie —
   * inaczej PUT save blob może cofnąć nagrodę (race między daily claim
   * a normalnym save flush).
   */
  gems_total: number;
  streak_days: number;
  next_claim_at: string;
  total_claimed: number;
  /**
   * Delta po claim (Faza 1b-5) — `{ currencies }` (zmieniony tylko gems).
   * Frontend aplikuje przez `runEconomyAction`/`applyEconomyDelta` (drift-guard).
   * `gems_total` zostaje jako autorytatywne save.gems dla UI/cache.
   */
  changes: EconomyDelta;
  /** Faza 1b-1 drift-guard — monotoniczny rev (klient ignoruje <= appliedRev). */
  save_rev: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Achievements API — Etap 5.D.2
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rodzaj osiągnięcia: `milestone` (odblokowuje się samo w akcji, nagroda 💎) albo
 * `album_biome` („Pełny album: <biom>" — gracz ODBIERA skiny przyciskiem).
 * Brak pola = starsze API sprzed nagród za album → traktuj jak `milestone`.
 */
export type AchievementKind = 'milestone' | 'album_biome';

/** Jeden achievement z `GET /achievements/me` (def + status per gracz). */
export interface AchievementView {
  id: string;
  kind?: AchievementKind;
  label: string;
  description: string;
  /** Klucz ikony PNG → `<Icon src="/ui/<iconKey>.webp">`. */
  iconKey: string;
  crystalsReward: number;
  unlocked: boolean;
  /** ISO timestamp odblokowania lub null. */
  unlockedAt: string | null;
  /** Tylko `album_biome`: biom, którego album trzeba skompletować. */
  biomeId?: string;
  /** Tylko `album_biome`: skompletowane wpisy albumu biomu / wszystkie pety biomu. */
  progress?: { done: number; total: number };
  /** Tylko `album_biome`: komplet jest, nagroda czeka na odbiór. */
  claimable?: boolean;
  /** Tylko `album_biome`: skiny dopisywane do `profile.unlockedSkins` przy odbiorze. */
  rewardSkinIds?: string[];
}

/** POST /achievements/:id/claim — delta (waluty + profil) + co odblokowano. */
export interface AchievementClaimResponse {
  changes: EconomyDelta;
  save_rev: number;
  achievement: AchievementUnlock;
  unlockedSkinIds: string[];
  /** `true` = już odebrane wcześniej (retry) — nic nowego nie przybyło. */
  alreadyClaimed: boolean;
}

/**
 * Unlock achievementu zwracany w odpowiedziach akcji (5.D.2d) — ujednolicony
 * kontrakt dla stall buy/list-pet i direct trade. `label` z serwera (źródło
 * prawdy) → toast nie duplikuje mapy id→label.
 */
export interface AchievementUnlock {
  id: string;
  label: string;
  crystals_reward: number;
}

export const achievementsApi = {
  /**
   * GET /achievements/me — pełna lista 5 definicji + status unlock gracza.
   * Wymaga sesji (gość OK — widzi same locked). 401 → global handler.
   */
  async me(): Promise<{ achievements: AchievementView[] }> {
    return request<{ achievements: AchievementView[] }>('/achievements/me', { method: 'GET' });
  },

  /**
   * POST /achievements/:id/claim — odbierz nagrodę „Pełny album: <biom>" (skiny
   * awatara). Serwer sam liczy komplet albumu. Wołać przez `runEconomyAction`
   * (delta `changes.profile` odświeża `unlockedSkins` → „Wygląd").
   * Błędy: 403 guest_achievement_locked, 404 unknown_achievement,
   * 409 album_incomplete | achievement_not_claimable | save_not_initialized.
   * Powtórny claim NIE jest błędem (200 + `alreadyClaimed: true`).
   */
  async claim(id: string): Promise<AchievementClaimResponse> {
    return request<AchievementClaimResponse>(`/achievements/${encodeURIComponent(id)}/claim`, {
      method: 'POST',
    });
  },
};

export const rewardsApi = {
  /**
   * GET /rewards/daily-status — czy claim dostępny TERAZ.
   * Frontend wywołuje przy bootstrap (po `setSaveLoaded`) żeby zdecydować
   * czy auto-otworzyć DailyRewardModal.
   */
  async getDailyStatus(): Promise<DailyStatusResponse> {
    return request<DailyStatusResponse>('/rewards/daily-status');
  },

  /**
   * POST /rewards/daily-claim — claim daily reward.
   * Sukces: 200 + gems_received. Cooldown nie przeszedł: 429 too_early
   * z `next_claim_at`.
   */
  async claimDaily(): Promise<DailyClaimResponse> {
    return request<DailyClaimResponse>('/rewards/daily-claim', { method: 'POST' });
  },
};

// Halloween: personal candy encounter, separate from the timed Atlantis fight.
export type { HalloweenBossView, BossCandy, BossGust } from '../../server/api/src/game-config/halloween-boss';
import type { HalloweenBossView } from '../../server/api/src/game-config/halloween-boss';
export interface HalloweenBossResponse extends HalloweenBossView {
  save_rev: number; changes: EconomyDelta; rewardGranted: number;
}
export const halloweenBossApi = {
  state: () => request<HalloweenBossView>('/events/halloween/boss', { method: 'GET' }),
  enter: () => request<HalloweenBossResponse>('/events/halloween/boss/enter', { method: 'POST', json: {} }),
  pulse: (fightId: string, pickupIds: string[], x: number, y: number) =>
    request<HalloweenBossResponse>('/events/halloween/boss/pulse', { method: 'POST', json: { fightId, pickupIds, x, y } }),
};
